import { useCallback, useEffect, useMemo, useState } from 'react';

import { Card, TokenExCardTypes } from '@givebacks/api/core';
import { Alert, Stack } from 'ui';
import { UseFormReturnType, hasLength, isNotEmpty } from 'ui/Form';

import {
  TokenEx,
  useFormListValidation,
  useFormListValidator,
  useTokenEx,
} from '../../../../hooks';
import { Validator } from '../../../../utils';
import './CardInfo.scss';

declare global {
  interface Window {
    TokenEx: any;
  }
}

const withCvv = true;

type CardInfoFieldsProps = {
  onCredentialChange: (credential?: TokenExCardTypes) => void;
};

export const CardInfoFields = ({ onCredentialChange }: CardInfoFieldsProps) => {
  const [error, setErrors] = useState<undefined | string>();

  const { useGetForm } = useFormListValidation<{
    donor_info?: UseFormReturnType<{ email: string }>;
    shop_to_give_enrollment?: UseFormReturnType<{ terms: boolean; enrollmentOption: boolean }>;
  }>();

  const donorInfoForm = useGetForm('donor_info');
  const stgForm = useGetForm('shop_to_give_enrollment');

  const showSaveCardCheckbox = useMemo(() => {
    return !stgForm?.values.enrollmentOption;
  }, [stgForm?.values.enrollmentOption]);

  const {
    cardType,
    errorStates: cardErrorState,
    isConfigAndTokenReady,
    initiateTokenEx,
    isCardValid,
    tokenizeCard,
  } = useTokenEx({
    tokenizeOnValidate: false,
    useRemoteConfig: true,
    useCvv: withCvv,
    onAutoFill(autoFillValues) {
      const { nameOnCard: name, month, year } = TokenEx.formatAutoFill(autoFillValues);
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      form.setValues({
        name,
        month,
        year,
      });
    },
  });

  const form = TokenEx.useTokenExForm({
    initialValues: {
      name: '',
      terms: true,
      month: '',
      year: '',
      zip: '',
      isCardValid: undefined,
      shouldSaveCard: false,
    },
    validate: {
      name: hasLength({ min: 2 }),
      terms: isNotEmpty(),
      month: (value) => (/^(0[1-9])$|^(1[0-2])$/.test(value) ? null : true),
      year: hasLength({ min: 4 }),
      zip: (value) => (String(value).trim().length >= 5 ? null : true),
      isCardValid: (value) => (value ? null : cardErrorState.message),
    },
    validateInputOnBlur: true,
  });

  // We'll use this callback to format the card data and return it to the to the caller of the tokenization function
  const handleTokenize = useCallback(async () => {
    const cardForm = (await tokenizeCard()) as Card;
    const { month, year, name, zip } = form.values;
    const isYearMonthValid = Validator.expirationDate({ month, year });
    if (isYearMonthValid.error) return setErrors(isYearMonthValid.error);
    if (cardForm) {
      const credential = {
        token: cardForm.token,
        hmac: cardForm.tokenHMAC,
        email: donorInfoForm?.values.email,
        save_for_future_use: form.values.shouldSaveCard,
        // Note: This is a very important field. If removing, consider if it should be handled elsewhere first.
        enroll_in_shop_to_give: stgForm?.values.terms,
        metadata: {
          card_type: cardForm.cardType,
          last_four: cardForm.lastFour,
          full_name: name,
          month: month,
          year: year,
          zip: zip,
        },
      };
      return credential;
    }
  }, [form.values, tokenizeCard, donorInfoForm?.values, stgForm?.values]);

  // Handle Card Validity and CardErrorStates
  useEffect(() => {
    if (isCardValid) {
      form.setFieldValue('isCardValid', true);
    } else {
      form.setFieldValue('isCardValid', undefined);
    }
    // otherwise, we'll set the form error state
    if (cardErrorState.isError) {
      form.setFieldError('isCardValid', cardErrorState.message);
    } else {
      form.clearFieldError('isCardValid');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardErrorState, isCardValid]);

  // Handle form.error changes
  useEffect(() => {
    // Check to see if there is an error on the form, if there is we'll decode it and set it to the error state
    const errorOne = Object.keys(form.errors)[0] as keyof typeof TokenEx.FormErrors;
    if (errorOne) {
      const errorMessage = errorOne == 'card' ? cardErrorState.message : undefined;
      setErrors(errorMessage ?? TokenEx.FormErrors[errorOne]);
    } else {
      setErrors(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only need to update the main error state when form.errors changes
  }, [form.errors]);

  useEffect(() => {
    onCredentialChange(cardType);
  }, [cardType, onCredentialChange]);

  useEffect(() => {
    isConfigAndTokenReady && initiateTokenEx('creditCard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigAndTokenReady]);

  // Set the global handler for the tokenization function
  TokenEx.useTokenize({ tokenizeCard: handleTokenize });
  useFormListValidator({ form, id: 'card_info' });

  return (
    <TokenEx.Provider form={form}>
      <form className="StripeCard mb-2" autoComplete="on">
        <Stack>
          <TokenEx.Fields withCvv={withCvv} withSaveCardCheckbox={showSaveCardCheckbox} />
          <Alert.Transition title={error} type="error" mounted={Boolean(error)} />
        </Stack>
      </form>
    </TokenEx.Provider>
  );
};
